'use client';

import { Accordion, AccordionDetails, AccordionSummary, Box } from '@mui/material';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import { useState } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';

export interface MeasurePDFaqProps {
  sectionTitle: string;
  q1Title: string;
  q1Body: string;
  q2Title: string;
  q2Body: string;
  q3Title: string;
  q3Body: string;
  q4Title: string;
  q4Intro: string;
  q4Bullet1: string;
  q4Bullet2: string;
  q4Bullet3: string;
  q4Outro: string;
}

const answerTextClass =
  'font-ir text-[12px] leading-[18px] pr-[10px] md:pr-[14px] 2xl:pr-[16px] md:text-[13px] 2xl:text-[14px] md:leading-[18px]';

const questionClass =
  'font-bb text-[14px] leading-[17px] pr-[10px] md:pr-[14px] 2xl:pr-[16px] md:text-[15px] 2xl:text-[16px] md:leading-[18px] 2xl:leading-[19px]';

export default function MeasurePDFaq({
  sectionTitle,
  q1Title,
  q1Body,
  q2Title,
  q2Body,
  q3Title,
  q3Body,
  q4Title,
  q4Intro,
  q4Bullet1,
  q4Bullet2,
  q4Bullet3,
  q4Outro,
}: MeasurePDFaqProps) {
  const items: Array<{ question: string; answer: ReactNode }> = [
    { question: q1Title, answer: <p className={answerTextClass}>{q1Body}</p> },
    { question: q2Title, answer: <p className={answerTextClass}>{q2Body}</p> },
    { question: q3Title, answer: <p className={answerTextClass}>{q3Body}</p> },
    {
      question: q4Title,
      answer: (
        <div className={answerTextClass}>
          <p className="mb-[16px] md:mb-[18px] 2xl:mb-[20px]">{q4Intro}</p>
          <ul className="mb-[16px] md:mb-[18px] 2xl:mb-[20px] list-disc space-y-[8px] md:space-y-[9px] 2xl:space-y-[10px] pl-5 marker:text-primary-black">
            <li>{q4Bullet1}</li>
            <li>{q4Bullet2}</li>
            <li>{q4Bullet3}</li>
          </ul>
          <p>{q4Outro}</p>
        </div>
      ),
    },
  ];

  const [expandedItems, setExpandedItems] = useState<number[]>([0]);

  const isExpanded = (index: number) => expandedItems.includes(index);

  const handleChange = (index: number) => (_event: SyntheticEvent, expanded: boolean) => {
    setExpandedItems((prev) => (expanded ? [...prev, index] : prev.filter((i) => i !== index)));
  };

  return (
    <section className="pt-[28px] md:pt-[0px] pb-[8px] md:pb-[0px] mb-[48px] max-w-[1024px] md:mb-[64px] 2xl:mb-[80px]">
      <h2 className="mb-[16px] md:mb-[24px] 2xl:mb-[32px] text-[20px] font-bb leading-[24px] md:text-[26px] 2xl:text-[32px] md:leading-[30px] 2xl:leading-[39px] text-center md:text-left">
        {sectionTitle}
      </h2>

      <Box component="ul" className="">
        {items.map((item, index) => (
          <Box
            component="li"
            key={index}
            className="border-t border-[#d4d4d4] pb-[16px] md:pb-[20px] 2xl:pb-[24px]"
          >
            <Accordion
              expanded={isExpanded(index)}
              onChange={handleChange(index)}
              sx={{
                backgroundColor: 'transparent',
                boxShadow: 'none',
                border: 'none',
                margin: 0,
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  margin: 0,
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <CollapseArrowDownOutlined className="text-[16px] text-[#a9a9a9] md:text-[#949494] 2xl:text-[#808080]" />
                }
                sx={{
                  padding: {
                    xs: '16px 0 0',
                    md: '24px 0 0',
                  },
                  minHeight: 'auto',
                  '& .MuiAccordionSummary-content': {
                    margin: '0 !important',
                  },
                  '&.Mui-expanded': {
                    minHeight: 'auto',
                    margin: 0,
                  },
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    transition: 'transform 0.3s ease',
                    transformOrigin: 'center',
                  },
                  '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                    transform: 'rotate(180deg)',
                  },
                }}
              >
                <h3 className={questionClass}>{item.question}</h3>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  padding: { xs: '8px 0 0', md: '10px 0 0' },
                  backgroundColor: 'transparent',
                }}
              >
                {item.answer}
              </AccordionDetails>
            </Accordion>
          </Box>
        ))}
      </Box>
    </section>
  );
}
